/**
 * `actionStatusMirror` — the action bar's **affordance mirror of the server's status-legality wall**,
 * so a Shut Down (⇒ Stunned) mech doesn't render SKIRMISH as a live button the authority will reject.
 *
 * **This re-enforces nothing — it CALLS the wall.** `checkActorStatusLegality`
 * (`packages/rules/src/encounter/statusLegality.ts`, exported from `@lancer-vtt/rules`) is the one
 * status-legality wall; this module hands it the row's action id and surfaces its verdict as the
 * row's title + interactivity. Keep no second hand-rolled status list here, and in particular no
 * local copy of `STUNNED_ACTION_EXCEPTIONS` — the wall's own exception set
 * (`act_boot_up`/`act_dismount`/`act_eject`) is what keeps BOOT UP live on a shut-down mech.
 *
 * **The id namespace is `act_<row id>` for a core row; a granted row (`<entry>:…`) passes its own id**,
 * which is what the Black Thumb Rodeo's idle wall matches (`t_black_thumb:*`). The wall's exception set is written in the `act_*`
 * namespace, and those three ids are the ONLY ids it ever reads `actionId` for — so prefixing the
 * core table's row id (`boot_up` → `act_boot_up`) is the correct and complete mapping for this call.
 * Do **not** reach for `actionIdForIntent` (`economy.ts`): that derives the *once-per-turn ledger*
 * namespace, which is a different, largely unprefixed one (`"overcharge"`, `"skirmish"`, …) — the two
 * namespaces coincide only by accident and confusing them silently unpins the Boot Up exception.
 *
 * **Why this call can only ever grey a row the server would also refuse**: with
 * `isAttack`/`tech`/`weapon` left unset, the wall's Jammed branches are unreachable — a non-attack,
 * non-tech verb passes them by construction — so the only branches that can return `err` are the
 * `cantAct` one (Stunned ∨ Shut Down ∨ Down and Out, minus the pinned exceptions) and the rodeo's
 * idle wall.
 *
 * **No known asymmetry.** The self-verbs that dispatch before the room's gate ladder (Hide, Search,
 * Disengage, Engage COMP/CON, Shut Down, Self-Destruct, Grapple break/release) now ring this same
 * wall inside their own referee gates (`packages/rules/src/encounter/statusLegality.ts` header;
 * `stunned-action-wall-bypass` Phase 3), so this mirror and the authority agree. **Do not grow a
 * bespoke allow-list here.**
 */
import { heldAction, heldLockoutReason, type EncounterState } from "@lancer-vtt/domain";
import { checkActorStatusLegality } from "@lancer-vtt/rules";

/**
 * The wall's verdict for one action row, as an affordance: `null` when the actor may take it,
 * otherwise the wall's own rules-cited rejection message (rendered as the static row's title, so a
 * greyed row explains itself rather than just going dead).
 *
 * @param rowId a core action table row id (`"skirmish"`, `"boot_up"`, …) or a granted action id —
 *   prefixed to the wall's `act_*` namespace here, never by the caller.
 */
export function actionStatusBlockReason(state: EncounterState, actorId: string, rowId: string): string | null {
  // A granted row's id (`t_black_thumb:2#Field Repair`) is already its ledger id, which the rodeo's
  // idle wall reads; only a core row is prefixed.
  const actionId = rowId.includes(":") ? rowId : `act_${rowId}`;
  const verdict = checkActorStatusLegality({ state, actor: actorId, actionId });
  return verdict.ok ? null : verdict.error;
}

/**
 * The **held-action lockout** as a per-row affordance: the engine's own rejection sentence while
 * `combatantId` is holding a prepared action, `null` otherwise.
 *
 * A unit holding a prepare may not move, act, or react until the hold triggers, is dropped, or
 * lapses (`packages/domain/src/encounter/model/turn.ts` {@link heldAction}'s doc). Every landing
 * site refuses it — the room's gate-ladder arm 1 (`backend/src/encounterGateLadder.ts`
 * `checkHeldActionLockout`), `boost` and `move_token` reducer-side, Overwatch and the reaction
 * registry — so **every** row on the bar is dead, not some subset. That is why this takes no row
 * id: the answer does not vary by row, and a caller that filtered would be inventing a rule.
 *
 * **The sentence is the engine's, via {@link heldLockoutReason}** — the same builder the gate
 * ladder's rejection uses, so the greyed row's title and the toast a click would raise are word
 * for word identical. Do not reword it here.
 *
 * Callers run this FIRST, ahead of {@link actionStatusBlockReason}: the lockout outranks status
 * legality (arm 1 runs before arm 9), so a holding-and-Stunned mech should read the lockout.
 * It is one map read per row per render — keep it that cheap.
 */
export function heldLockoutBlockReason(state: EncounterState, combatantId: string): string | null {
  const held = heldAction(state.activation, combatantId);
  return held === null ? null : heldLockoutReason(combatantId, held);
}
